import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2, X } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Progress } from "../ui/progress";
import { adminApi, errText } from "./api";
import { Field, SaveBtn, Panel, inputCls } from "./Fields";
import { SETUP } from "../../data/site";
import { VideoEmbed } from "../site/VideoEmbed";

const VideoField = ({ id, value, onChange }) => {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) { toast.error("Video en fazla 150 MB olabilir"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setPct(0);
    try {
      const { data } = await adminApi.post("/admin/videos", fd, {
        onUploadProgress: (e) => setPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      onChange(data.url);
      toast.success("Video yüklendi — kaydetmeyi unutma");
    } catch (ex) {
      toast.error(errText(ex));
    } finally {
      setPct(null);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Field id={`video-${id}`} label="Video (YouTube bağlantısı veya yüklenen dosya)" placeholder="https://youtu.be/..."
            value={value || ""} onChange={(e) => onChange(e.target.value.trim())} data-testid={`admin-guide-video-${id}`} />
        </div>
        <div className="flex items-end gap-2">
          <input ref={ref} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid={`admin-guide-file-${id}`} />
          <button type="button" onClick={() => ref.current?.click()} disabled={pct !== null} data-testid={`admin-guide-upload-${id}`}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-white/[0.06] px-4 text-sm font-semibold ring-1 ring-white/10 hover:bg-white/[0.1] disabled:opacity-60">
            {pct !== null ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Dosya yükle
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} aria-label="Videoyu kaldır" data-testid={`admin-guide-video-clear-${id}`}
              className="grid h-11 w-11 place-items-center rounded-xl ring-1 ring-white/10 text-red-300 hover:bg-red-500/10"><X size={16} /></button>
          )}
        </div>
      </div>
      {pct !== null && (
        <div className="space-y-1">
          <Progress value={pct} className="h-2 bg-white/10" />
          <p className="text-xs text-[#c9c6f0]">Yükleniyor… %{pct}</p>
        </div>
      )}
      {value && <div className="max-w-md"><VideoEmbed url={value} title="Önizleme" /></div>}
    </div>
  );
};

const GuideCard = ({ s, g, set }) => {
  const steps = g.steps;
  const upStep = (i, v) => set({ ...g, steps: steps.map((x, j) => (j === i ? v : x)) });
  return (
    <Panel>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p lang="en" className="text-[11px] uppercase tracking-[0.25em] text-[#a4a6ff]">{s.app}</p>
          <p className="font-display text-lg font-semibold">{s.title}</p>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {steps.map((t, i) => (
          <div key={i} className="flex gap-3">
            <span className="mt-2 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#5b5fd6] text-sm font-bold">{i + 1}</span>
            <Textarea rows={2} value={t} onChange={(e) => upStep(i, e.target.value)} className={`${inputCls} h-auto min-h-[64px]`}
              data-testid={`admin-guide-step-${s.id}-${i + 1}`} />
            <button type="button" disabled={steps.length <= 1} onClick={() => set({ ...g, steps: steps.filter((_, j) => j !== i) })}
              aria-label="Adımı sil" data-testid={`admin-guide-step-remove-${s.id}-${i + 1}`}
              className="mt-2 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-300 hover:bg-red-500/10 disabled:opacity-30"><Trash2 size={15} /></button>
          </div>
        ))}
        {steps.length < 6 && (
          <button type="button" onClick={() => set({ ...g, steps: [...steps, ""] })} data-testid={`admin-guide-step-add-${s.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#a4a6ff] hover:text-white"><Plus size={15} /> Adım ekle</button>
        )}
      </div>
      <div className="mt-6 border-t border-white/[0.08] pt-5">
        <VideoField id={s.id} value={g.video} onChange={(v) => set({ ...g, video: v })} />
      </div>
    </Panel>
  );
};

export const GuidesEditor = ({ initial, legacyVideos, onSaved }) => {
  const [data, setData] = useState(() => Object.fromEntries(SETUP.map((s) => {
    const g = initial?.[s.id] || {};
    return [s.id, { steps: g.steps?.length ? g.steps : s.steps, video: g.video || legacyVideos?.[s.id] || "" }];
  })));
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.put("/admin/guides", data);
      toast.success("Kurulum rehberleri kaydedildi");
      onSaved();
    } catch (ex) {
      toast.error(errText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="admin-guides-form">
      {SETUP.map((s) => <GuideCard key={s.id} s={s} g={data[s.id]} set={(g) => setData({ ...data, [s.id]: g })} />)}
      <div className="sticky bottom-4 flex justify-end">
        <SaveBtn busy={busy} data-testid="admin-guides-save">Rehberleri Kaydet</SaveBtn>
      </div>
    </form>
  );
};
